import * as utils from './utils';

function assert(value, message) {
  if (!value) {
    throw new Error(message);
  }
}

/**
 * Delegates constructor and returns new object
 */
export default class EventstreamClient {
  constructor(...args) {
    this.initialize(...args);

    // Constants
    this.EVENTSTREAM_ENDPOINT = '/1.0/log/pixel_tracking';
    this.EVENTSTREAM_V2_ENDPOINT = '/2.0/log/pixel_tracking';
    this.ANONYMOUS_VISITOR_TOKEN_COOKIE = '_savt';
    this.TRACK_COOKIE = 'track_cookie';
    this.OPTIMIZELY_END_USER_ID_COOKIE = 'optimizelyEndUserId';
    this.IDENTIFIED_VISITOR_TOKEN_PARAM = 'ivt';
    this.ANONYMOUS_VISITOR_TOKEN_PARAM = 'avt';
    this.MERCHANT_TOKEN_PARAM = 'mct';
    this.SESSION_TOKEN_PARAM = 'st';
    this.UNIT_TOKEN_PARAM = 'unt';
    this.PERSON_TOKEN_PARAM = 'prt';
    this.EMPLOYMENT_TOKEN_PARAM = 'emt';
    this.PAYMENT_TOKEN_PARAM = 'pmt';
    this.LIBRARY_NAME = 'js/eventstream';
    this.LIBRARY_VERSION = '__VERSION__';
    this.ES2_COOKIE_PREFIX_MAP = {
      f: 'track_cookie_forecast_channel',
      d: 'track_cookie_detail_channel',
      p: 'track_cookie_partner_channel',
      c: 'track_cookie_campaign_channel',
      o: 'track_cookie_offer_channel',
      u: 'track_cookie_url_channel',
      l: 'track_cookie_location_channel',
      dt: 'track_cookie_device_channel',
      pr: 'track_cookie_product_channel',
      sq: 'track_cookie_marin_tag'
    };

    // Used for cachebusting. See _urlFor* methods.
    this.callCount = 0;
  }

  /**
   * Set up object with local state and configuration
   *
   * @param {Object} options
   */
  initialize(options) {
    assert(
      options.applicationName,
      'EventstreamClient.initialize: applicationName is required'
    );
    assert(
      options.environment,
      'EventstreamClient.initialize: environment is required'
    );
    this.applicationName = options.applicationName;
    this.environment = options.environment;
    this.anonymousId = options.anonymousId;
    this.userId = options.userId;
    this.merchantId = options.merchantId;
    this.unitId = options.unitId;
    this.personId = options.personId;
    this.employmentId = options.employmentId;
    this.paymentId = options.paymentId;
    this.defaultFields = options.defaultFields;
    this.sessionId = options.sessionId;
    this.utmCampaignData = options.utmCampaignData || {};
  }

  /**
   * Tracks a page view. Schema defined here: https://schema-manager.stage.sqprod.co/schema-manager/staging/catalog/page_view
   * If no params trackV2WithDefaults will do its best to fill all the fields.
   * TODO: figure out how to track the device catalog (perhaps server-side)
   */
  trackV2PageView(params = {}) {
    this.trackV2WithDefaults(
      utils.mergeObjects([{ catalog_name: 'page_view' }, params])
    );
  }

  /*
* Tracks a page click.
* Schema: https://schema-manager.stage.sqprod.co/schema-manager/staging/catalog/page_click
* TODO: figure out how to track the device catalog (perhaps server-side)
*/
  trackV2PageClick(client_params) {
    const params = {
      catalog_name: 'page_click',
      page_click_action: client_params.action,
      page_click_description: client_params.description
    };

    if (client_params.detail) {
      params.page_click_detail = client_params.detail;
    }

    this.trackV2WithDefaults(params);
  }

  /**
   * Tracks an event and tries to fill in all webpage, screen and browser params.
   * Must pass in catalog_name in the params. Passed in params will overwrite
   * any that this function gathers.
   */
  trackV2WithDefaults(params = {}) {
    let toTrack = {};
    const webpageData = utils.getPageMetrics();
    for (const webpageProp in webpageData) {
      toTrack[`webpage_${webpageProp}`] = webpageData[webpageProp];
    }

    /**
     * The order of precedence for the umtData that gets passed to trackV2 from low to high is:
     *  1. initialized values (values passed to the method initialize)
     *  2. queryParams (retrieved via getUtmCampaignData)
     *  3. trackV2WithDefaults params
     * Initialized values have the lowest precedence as they are the least dynamic. They should be
     * set only on initialization. queryParams can possibly change as the user navigates around.
     * trackV2WithDefaults params have the highest priority as they have to be set intentionally
     * each time.
     */
    const utmData = Object.assign(
      {},
      this.utmCampaignData,
      utils.getUtmCampaignData()
    );

    for (const utmProp in utmData) {
      toTrack[`webpage_${utmProp}`] = utmData[utmProp];
    }

    toTrack = utils.mergeObjects([
      toTrack,
      utils.getBrowserAndOSMetrics(),
      utils.getScreenMetricsV2(),
      // params will overwrite anything set in toTrack
      params
    ]);

    this.trackV2(toTrack);
  }

  /**
   * Track a new event with data
   * Page views are usually logged with eventName = 'PageView' and eventValue = <url>
   *
   * @param {String} eventName (required)
   * @param {String} eventValue (required)
   * @param {Object} rawData (optional)
   */
  track(params) {
    assert(params.eventName, 'EventstreamClient.track: eventName is required');
    assert(
      params.eventValue,
      'EventstreamClient.track: eventValue is required'
    );

    const eventName = utils.filterUrl(params.eventName);
    const eventValue = utils.filterUrl(params.eventValue);
    let rawData = this._applyDefaults(params.rawData || {});
    const anonymousId =
      params.anonymousId ||
      this.anonymousId ||
      this._getCookieValue(this.ANONYMOUS_VISITOR_TOKEN_COOKIE);
    const trackCookie = this._getEncodedCookieValue(this.TRACK_COOKIE);
    const optimizelyEndUserIdCookie = this._getCookieValue(
      this.OPTIMIZELY_END_USER_ID_COOKIE
    );
    const userId = params.userId || this.userId;

    const merchantId = params.merchantId || this.merchantId;
    const unitId = params.unitId || this.unitId;
    const personId = params.personId || this.personId;
    const employmentId = params.employmentId || this.employmentId;
    const paymentId = params.paymentId || this.paymentId;
    const sessionId = params.sessionId || this.sessionId;

    const callback = params.callback || null;

    if (this._getApiHost() === null) {
      return;
    }

    const rawDataArray = [utils.getMetrics(), rawData];
    // add the track cookie to raw data if it's set
    if (trackCookie) {
      rawDataArray.push({ track_cookie: trackCookie });
    }
    // add the optimizely end user id cookie to raw data if it's set
    if (optimizelyEndUserIdCookie) {
      rawDataArray.push({
        optimizely_end_user_id_cookie: optimizelyEndUserIdCookie
      });
    }
    rawData = utils.mergeObjects(rawDataArray);

    const image = new Image();

    if (typeof callback === 'function') {
      utils.addEvent(image, 'load', callback, true);
    }

    image.src = this._urlFor(
      eventName,
      eventValue,
      rawData,
      anonymousId,
      userId,
      merchantId,
      unitId,
      personId,
      employmentId,
      paymentId,
      sessionId
    );
  }

  /**
   * Tracks an ES2 event.
   *
   * @Param {String} catalog_name (required)
   * All other fields are dynamic depending on the catalog_name.
   * Schemas for this object must be pre-registered at go/schema-manager.
   * Track cookie data is included in this method.
   */
  trackV2(params) {
    assert(
      params.catalog_name,
      'EventstreamClient.trackV2: catalog_name is required'
    );
    if (this._getApiHost() === null) {
      return;
    }

    const data = {
      app_name: this.applicationName,
      u_library_name: this.LIBRARY_NAME,
      u_library_version: this.LIBRARY_VERSION
    };

    // First we get all the tokens, set them appropriately and then clear them.
    const anonymousId =
      params.anonymousId ||
      this.anonymousId ||
      this._getCookieValue(this.ANONYMOUS_VISITOR_TOKEN_COOKIE);
    params['subject_anonymous_token'] = anonymousId;
    params.anonymousId = undefined;

    const userId = params.userId || this.userId;
    params['subject_user_token'] = userId;
    params.userId = undefined;

    const merchantId = params.merchantId || this.merchantId;
    params['subject_merchant_token'] = merchantId;
    params.merchantId = undefined;

    const unitId = params.unitId || this.unitId;
    params['subject_unit_token'] = unitId;
    params.unitId = undefined;

    const personId = params.personId || this.personId;
    params['subject_person_token'] = personId;
    params.personId = undefined;

    const employmentId = params.employmentId || this.employmentId;
    params['subject_employment_token'] = employmentId;
    params.employmentId = undefined;

    const paymentId = params.paymentId || this.paymentId;
    params['subject_payment_token'] = paymentId;
    params.paymentId = undefined;

    const sessionId = params.sessionId || this.sessionId;
    params['session_token'] = sessionId;
    params.sessionId = undefined;

    // pull out callback so we don't try to stuff it in our URL
    const callback = params.callback || null;
    params.callback = undefined;

    // Then we get all the data for the event.
    const paramKeys = Object.keys(params);
    let key;
    let value;
    for (let i = 0; i < paramKeys.length; i++) {
      key = paramKeys[i];
      value = params[key];
      if (value !== undefined && value !== null) {
        data[key] = value;
      }
    }

    // Then we add track cookie data
    this._addTrackCookiesToData(data);

    const image = new Image();

    if (typeof callback === 'function') {
      utils.addEvent(image, 'load', callback, true);
    }

    image.src = this._es2UrlFor(data);
  }

  /**
   * [Private] Helper for making tracking data into URL params for ES2
   *
   * Because we expect a simple KV map, no need for special keys like in _urlFor.
   */
  _es2UrlFor(data) {
    const queryString = [];

    const keys = Object.keys(data);
    let key;
    for (let i = 0; i < keys.length; i++) {
      key = keys[i];
      queryString.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`
      );
    }

    // Make sure the browser doesn't cache this request
    this.callCount += 1;
    queryString.push(String(`nocache=${Date.now()}`) + this.callCount);

    return `${this._getApiHost() +
      this.EVENTSTREAM_V2_ENDPOINT}?${queryString.join('&')}`;
  }

  /**
   * [Private] Helper for applying previously-set default fields to rawData
   *
   * @param {Object} rawData (required)
   * @return {Object} defaults + rawData
   */
  _applyDefaults(rawData) {
    if (!this.defaultFields) {
      return rawData;
    }
    return utils.mergeObjects([this.defaultFields, rawData]);
  }

  /**
   * [Private] Create logging url for a given event
   *
   * @param {Array(eventName, eventValue, eventData)} event (required)
   * @return {string}
   */
  _urlFor(
    eventName,
    eventValue,
    rawData,
    anonymousId,
    userId,
    merchantId,
    unitId,
    personId,
    employmentId,
    paymentId,
    sessionId
  ) {
    const queryString = [];

    // Attach anonymous visitor token (if present)
    if (anonymousId) {
      queryString.push(
        `${this.ANONYMOUS_VISITOR_TOKEN_PARAM}=${encodeURIComponent(
          anonymousId
        )}`
      );
    }

    // Attach identified visitor token (if present)
    if (userId) {
      queryString.push(
        `${this.IDENTIFIED_VISITOR_TOKEN_PARAM}=${encodeURIComponent(userId)}`
      );
    }

    // Attach ELDM (merchant/unit/person/employment/payment) tokens (if present)
    if (merchantId) {
      queryString.push(
        `${this.MERCHANT_TOKEN_PARAM}=${encodeURIComponent(merchantId)}`
      );
    }
    if (unitId) {
      queryString.push(
        `${this.UNIT_TOKEN_PARAM}=${encodeURIComponent(unitId)}`
      );
    }
    if (personId) {
      queryString.push(
        `${this.PERSON_TOKEN_PARAM}=${encodeURIComponent(personId)}`
      );
    }
    if (employmentId) {
      queryString.push(
        `${this.EMPLOYMENT_TOKEN_PARAM}=${encodeURIComponent(employmentId)}`
      );
    }
    if (paymentId) {
      queryString.push(
        `${this.PAYMENT_TOKEN_PARAM}=${encodeURIComponent(paymentId)}`
      );
    }
    if (sessionId) {
      queryString.push(
        `${this.SESSION_TOKEN_PARAM}=${encodeURIComponent(sessionId)}`
      );
    }

    // Attach required parameters
    queryString.push(`app_name=${encodeURIComponent(this.applicationName)}`);
    queryString.push(`n=${encodeURIComponent(eventName)}`);
    queryString.push(`v=${encodeURIComponent(eventValue)}`);
    queryString.push(`m=${encodeURIComponent(JSON.stringify(rawData))}`);
    queryString.push(`ln=${encodeURIComponent(this.LIBRARY_NAME)}`);
    queryString.push(`lv=${encodeURIComponent(this.LIBRARY_VERSION)}`);

    // Make sure the browser doesn't cache this request
    this.callCount += 1;
    queryString.push(String(`nocache=${Date.now()}`) + this.callCount);

    return `${this._getApiHost() +
      this.EVENTSTREAM_ENDPOINT}?${queryString.join('&')}`;
  }

  /**
   * [Private] Get EventstreamClient API host name based on environment parameter
   *
   * @return {string}
   */
  _getApiHost() {
    if (this.environment === 'production') {
      return 'https://api.squareup.com';
    }
    if (this.environment === 'sandbox') {
      return 'https://api.squareupsandbox.com';
    }
    if (this.environment === 'staging') {
      return 'https://api.squareupstaging.com';
    }
    if (this.environment === 'development') {
      return 'https://api.squareupstaging.com';
    }
    if (this.environment === 'test-eventstream') {
      return 'https://eventstream.example.com';
    }
    return null;
  }

  /**
   * [Private] Get cookie value given a cookie name
   *
   * @param {String} cookieName (required)
   * @return {string}
   */
  _getCookieValue(cookieName) {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookieData = cookies[i].split('=');
      const name = cookieData[0].replace(/^\s+|\s+$/g, '');
      if (name === cookieName) {
        return decodeURIComponent(cookieData[1]);
      }
    }
    return null;
  }

  /**
   * [Private] Get encoded cookie value given a cookie name
   *
   * @param {String} cookieName (required)
   * @return {string}
   */
  _getEncodedCookieValue(cookieName) {
    const value = this._getCookieValue(cookieName);
    return value ? atob(value.split('--')[0]) : null;
  }

  /**
   * [Private] Get tracking cookie values in a map for ES2
   *
   * @param {Object} data (required): the data object to track
   */
  _addTrackCookiesToData(data) {
    let trackCookieArray;
    try {
      trackCookieArray = JSON.parse(
        this._getEncodedCookieValue(this.TRACK_COOKIE)
      );
    } catch (e) {
      return;
    }
    if (trackCookieArray) {
      let fullCookie;
      let trackCookieKey;
      let underscoreSplitCookie;
      let dashSplitCookie;
      for (let i = 0; i < trackCookieArray.length; i++) {
        fullCookie = trackCookieArray[i];
        underscoreSplitCookie = fullCookie.split('_')[0];
        trackCookieKey = this.ES2_COOKIE_PREFIX_MAP[underscoreSplitCookie];
        if (trackCookieKey === undefined || trackCookieKey === null) {
          dashSplitCookie = fullCookie.split('-')[0];
          trackCookieKey = this.ES2_COOKIE_PREFIX_MAP[dashSplitCookie];
        }
        if (trackCookieKey !== undefined || trackCookieKey !== null) {
          data[trackCookieKey] = fullCookie;
        }
      }
    }
  }
}
